import { useCallback, useEffect, useRef, useState } from 'react';
import { GOOGLE_API_KEY } from '../../config';
import axios from '../../utils/axios';
import { loadGoogleMapsApi } from '../../utils/googleMapsLoader';
import { geofencesEqual, normalizeGeofence, computeDefaultIngressEgress } from '../../utils/geofenceUtils';
import {
    geofenceFailureMessage,
    GEOFENCE_MAP_LOAD_FAILED,
    GEOFENCE_QUERY_FAILED,
} from '../../utils/geofenceMessages';
import { toastError, toastWarn } from '../../utils/toast';
import greenLocationMarker from '../../assets/icons/green-location-marker.svg';
import redLocationMarker from '../../assets/icons/red-location-marker.svg';

const BASIC_POLYGON_SIZE_M = { w: 40, h: 30 };
const EDGE_GRADIENT_ORANGE = '#FF5A1F';
const EDGE_GRADIENT_BLUE = '#1E40AF';
const POLYGON_EDITABLE_TOOLS = new Set(['drag']);

const ENTRANCE_CURSOR_SVG = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32"><path d="M12 0C7.03 0 3 4.03 3 9c0 6.75 9 23 9 23s9-16.25 9-23c0-4.97-4.03-9-9-9z" fill="#16A34A"/><text x="12" y="10.5" text-anchor="middle" dominant-baseline="central" fill="#fff" font-family="Arial,sans-serif" font-size="11" font-weight="900">E</text></svg>',
);
const EXIT_CURSOR_SVG = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="32" viewBox="0 0 24 32"><path d="M12 0C7.03 0 3 4.03 3 9c0 6.75 9 23 9 23s9-16.25 9-23c0-4.97-4.03-9-9-9z" fill="#CC0000"/><text x="12" y="10.5" text-anchor="middle" dominant-baseline="central" fill="#fff" font-family="Arial,sans-serif" font-size="11" font-weight="900">X</text></svg>',
);
const ENTRANCE_CURSOR = `url("data:image/svg+xml,${ENTRANCE_CURSOR_SVG}") 12 32, crosshair`;
const EXIT_CURSOR = `url("data:image/svg+xml,${EXIT_CURSOR_SVG}") 12 32, crosshair`;

function basicRectangleAt(lat, lng) {
    const latDeg = (BASIC_POLYGON_SIZE_M.h / 2) / 111_320;
    const lngDeg = (BASIC_POLYGON_SIZE_M.w / 2) / (111_320 * Math.cos((lat * Math.PI) / 180));
    return [
        { lat: lat - latDeg, lng: lng - lngDeg },
        { lat: lat - latDeg, lng: lng + lngDeg },
        { lat: lat + latDeg, lng: lng + lngDeg },
        { lat: lat + latDeg, lng: lng - lngDeg },
    ];
}

function lerpColor(c1, c2, t) {
    const parse = (hex) => {
        const n = parseInt(hex.slice(1), 16);
        return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
    };
    const a = parse(c1);
    const b = parse(c2);
    const r = Math.round(a.r + (b.r - a.r) * t);
    const g = Math.round(a.g + (b.g - a.g) * t);
    const bl = Math.round(a.b + (b.b - a.b) * t);
    return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`;
}

function boundsFromAddress(addressBounds) {
    if (!Array.isArray(addressBounds) || addressBounds.length < 2) return null;
    const lats = addressBounds.map(p => p[0]);
    const lngs = addressBounds.map(p => p[1]);
    return {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east:  Math.max(...lngs),
        west:  Math.min(...lngs),
    };
}

function coordsFromPolygons(polygonsRef) {
    return polygonsRef.current
        .map(poly => {
            const path = poly.getPath();
            const coords = [];
            for (let i = 0; i < path.getLength(); i++) {
                const pt = path.getAt(i);
                coords.push({ lat: pt.lat(), lng: pt.lng() });
            }
            return coords;
        })
        .filter(c => c.length >= 3);
}

const GeofenceEditor = ({
    embedded = false,
    listingId,
    addressData,
    initialPolygon,
    initialIngress = [],
    initialEgress = [],
    onAutosave,
    onPolygonChange,
    onRegenUpdate,
    onOsmResult,
    skipAutoQuery = false,
    activeTool = 'drag',
    activeLayer = 'all',
    layers = [],
    onChangeTool,
    onPolygonsChange,
}) => {
    const mapDivRef = useRef(null);
    const googleRef = useRef(null);
    const mapRef = useRef(null);
    const polygonsRef = useRef([]);
    const pathListenersRef = useRef([]);
    const customDrawRef = useRef(null);
    const osmQueryFiredRef = useRef(false);
    const osmInFlightRef = useRef(false);
    const autosaveTimerRef = useRef(null);
    const polygonsExistRef = useRef(false);
    const hadPolygonsEverRef = useRef(normalizeGeofence(initialPolygon).length > 0);
    const initialPolygonAppliedRef = useRef(false);
    const initialIngressEgressAppliedRef = useRef(false);
    const initialDrawDoneRef = useRef(false);
    const entranceMarkerRef = useRef(null);
    const egressMarkerRef = useRef(null);
    const ingressRef = useRef(Array.isArray(initialIngress) ? initialIngress : []);
    const egressRef = useRef(Array.isArray(initialEgress) ? initialEgress : []);
    const selectedMarkerRef = useRef(null);
    const scheduleAutosaveRef = useRef(null);
    const fitDoneForAddressRef = useRef(null);
    // Fire onOsmResult(hasBoundary) exactly once per lifetime — see comment on
    // the callback fanout at the OSM query / skip paths below.
    const osmResultReportedRef = useRef(false);
    const reportOsmResult = useCallback((hasBoundary) => {
        if (osmResultReportedRef.current) return;
        osmResultReportedRef.current = true;
        onOsmResult?.(Boolean(hasBoundary));
    }, [onOsmResult]);

    const [polygons, setPolygons] = useState(() => normalizeGeofence(initialPolygon));
    const [ingress, setIngressState] = useState(() =>
        Array.isArray(initialIngress) ? initialIngress : []);
    const [egress, setEgressState] = useState(() =>
        Array.isArray(initialEgress) ? initialEgress : []);
    const [osmLoading, setOsmLoading] = useState(false);
    const [osmSource, setOsmSource] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [mapError, setMapError] = useState(false);
    const geofenceToastShownRef = useRef(null);
    const selectedPolyIdxRef = useRef(null);
    const [deleteBtnPos, setDeleteBtnPos] = useState(null);
    const activeToolRef = useRef(activeTool);
    const activeLayerRef = useRef(activeLayer);
    const layersRef = useRef(layers);
    const selectedVertexRef = useRef(null);
    const gradientPolylinesRef = useRef([]);
    const selectVertexMarkersRef = useRef([]);
    const selectVertexMarkerListenersRef = useRef([]);
    const selectVertexRef = useRef(null);
    const rebuildSelectVertexMarkersRef = useRef(null);
    const prevActiveToolRef = useRef(activeTool);
    const [vertexDeleteBtnPos, setVertexDeleteBtnPos] = useState(null);

    useEffect(() => { activeToolRef.current = activeTool; }, [activeTool]);
    useEffect(() => { activeLayerRef.current = activeLayer; }, [activeLayer]);
    useEffect(() => { layersRef.current = layers; }, [layers]);

    useEffect(() => { ingressRef.current = ingress; }, [ingress]);
    useEffect(() => { egressRef.current = egress; }, [egress]);

    const computeDeleteBtnPos = useCallback((poly) => {
        const map = mapRef.current;
        if (!map || !mapDivRef.current) return null;
        const bounds = map.getBounds();
        if (!bounds) return null;

        const path = poly.getPath();
        const n = path.getLength();
        if (n === 0) return null;

        let maxLat = -Infinity, sumLng = 0;
        for (let i = 0; i < n; i++) {
            const pt = path.getAt(i);
            if (pt.lat() > maxLat) maxLat = pt.lat();
            sumLng += pt.lng();
        }
        const centerLng = sumLng / n;

        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latRange = ne.lat() - sw.lat();
        const lngRange = ne.lng() - sw.lng();
        if (latRange === 0 || lngRange === 0) return null;

        const { offsetWidth: w, offsetHeight: h } = mapDivRef.current;
        return {
            x: Math.round(((centerLng - sw.lng()) / lngRange) * w),
            y: Math.round(((ne.lat() - maxLat)  / latRange) * h),
        };
    }, []);

    const deselectPolygon = useCallback(() => {
        const idx = selectedPolyIdxRef.current;
        if (idx !== null && polygonsRef.current[idx]) {
            polygonsRef.current[idx].setOptions({
                strokeColor: '#2254A3', fillColor: '#1E52A3', fillOpacity: 0.35,
            });
        }
        selectedPolyIdxRef.current = null;
        setDeleteBtnPos(null);
    }, []);

    const doSelectPolygon = useCallback((idx) => {
        const prev = selectedPolyIdxRef.current;
        if (prev !== null && polygonsRef.current[prev]) {
            polygonsRef.current[prev].setOptions({
                strokeColor: '#2254A3', fillColor: '#1E52A3', fillOpacity: 0.35,
            });
        }
        if (idx === null || !polygonsRef.current[idx]) {
            selectedPolyIdxRef.current = null;
            setDeleteBtnPos(null);
            return;
        }
        selectedMarkerRef.current = null;
        polygonsRef.current[idx].setOptions({
            strokeColor: '#EF4444', fillColor: '#DC2626', fillOpacity: 0.45,
        });
        selectedPolyIdxRef.current = idx;
        setTimeout(() => {
            const pos = computeDeleteBtnPos(polygonsRef.current[idx]);
            setDeleteBtnPos(pos);
        }, 0);
    }, [computeDeleteBtnPos]);

    const buildAutosavePayload = useCallback((geofenceOverride) => ({
        geofence: geofenceOverride ?? coordsFromPolygons(polygonsRef),
        ingress: ingressRef.current,
        egress: egressRef.current,
    }), []);

    const flushAutosave = useCallback((partial = {}) => {
        if (!onAutosave) return;
        const payload = { ...buildAutosavePayload(), ...partial };
        onAutosave(payload);
    }, [onAutosave, buildAutosavePayload]);

    const scheduleAutosave = useCallback((partial = {}) => {
        if (!onAutosave) return;
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = setTimeout(() => {
            flushAutosave(partial);
        }, 500);
    }, [onAutosave, flushAutosave]);

    useEffect(() => {
        scheduleAutosaveRef.current = scheduleAutosave;
    }, [scheduleAutosave]);

    const destroyCustomDraw = useCallback(() => {
        const session = customDrawRef.current;
        if (!session) {
            return;
        }
        session.listeners?.forEach((l) => {
            googleRef.current?.maps?.event.removeListener(l);
        });
        session.preview?.setPath([]);
        session.polyline?.setMap(null);
        session.halo?.setMap(null);
        session.preview?.setMap(null);
        session.markers?.forEach((m) => m.setMap(null));
        customDrawRef.current = null;
        if (mapRef.current) {
            mapRef.current.setOptions({
                draggableCursor: null,
                draggingCursor: null,
                disableDoubleClickZoom: false,
            });
        }
    }, []);

    const cancelCustomDraw = useCallback(() => {
        destroyCustomDraw();
    }, [destroyCustomDraw]);

    const startCustomDrawRef = useRef(null);
    const addCustomDrawVertexRef = useRef(null);
    const deselectPolygonRef = useRef(null);
    const computeDeleteBtnPosRef = useRef(null);
    const cancelDrawingRef = useRef(null);
    const finishCustomDrawRef = useRef(null);
    const placeEntranceMarkerRef = useRef(null);
    const placeEgressMarkerRef = useRef(null);
    const handleBasicPolygonDropRef = useRef(null);
    const handleEntranceDropRef = useRef(null);
    const handleExitDropRef = useRef(null);
    const clearVertexSelectionRef = useRef(null);

    const syncCustomDrawPolylines = useCallback((session) => {
        if (!session?.polyline || !googleRef.current) return;
        const path = session.vertices.map(
            (v) => new googleRef.current.maps.LatLng(v.lat, v.lng),
        );
        session.polyline.setPath(path);
        session.halo?.setPath(path);
        if (session.vertices.length === 0) {
            session.preview?.setPath([]);
        }
    }, []);

    const destroyAllPolygons = useCallback(() => {
        selectedPolyIdxRef.current = null;
        setDeleteBtnPos(null);
        selectedVertexRef.current = null;
        setVertexDeleteBtnPos(null);
        gradientPolylinesRef.current.forEach((line) => line.setMap(null));
        gradientPolylinesRef.current = [];
        if (googleRef.current) {
            selectVertexMarkerListenersRef.current.forEach(l =>
                googleRef.current.maps.event.removeListener(l),
            );
            selectVertexMarkerListenersRef.current = [];
            selectVertexMarkersRef.current.forEach(m => {
                googleRef.current.maps.event.clearInstanceListeners(m);
                m.setMap(null);
            });
            selectVertexMarkersRef.current = [];
        }
        if (!googleRef.current) return;
        pathListenersRef.current.forEach(l =>
            googleRef.current.maps.event.removeListener(l)
        );
        pathListenersRef.current = [];
        polygonsRef.current.forEach(poly => {
            googleRef.current.maps.event.clearInstanceListeners(poly);
            poly.setMap(null);
        });
        polygonsRef.current = [];
    }, []);

    const destroyMarkers = useCallback(() => {
        if (entranceMarkerRef.current) {
            googleRef.current?.maps?.event.clearInstanceListeners(entranceMarkerRef.current);
            entranceMarkerRef.current.setMap(null);
            entranceMarkerRef.current = null;
        }
        if (egressMarkerRef.current) {
            googleRef.current?.maps?.event.clearInstanceListeners(egressMarkerRef.current);
            egressMarkerRef.current.setMap(null);
            egressMarkerRef.current = null;
        }
        selectedMarkerRef.current = null;
    }, []);

    const removeMarker = useCallback((type, { skipAutosave = false } = {}) => {
        if (type === 'entrance') {
            if (entranceMarkerRef.current) {
                googleRef.current?.maps?.event.clearInstanceListeners(entranceMarkerRef.current);
                entranceMarkerRef.current.setMap(null);
                entranceMarkerRef.current = null;
            }
            setIngressState([]);
            ingressRef.current = [];
        } else {
            if (egressMarkerRef.current) {
                googleRef.current?.maps?.event.clearInstanceListeners(egressMarkerRef.current);
                egressMarkerRef.current.setMap(null);
                egressMarkerRef.current = null;
            }
            setEgressState([]);
            egressRef.current = [];
        }
        if (selectedMarkerRef.current === type) selectedMarkerRef.current = null;
        if (!skipAutosave) scheduleAutosaveRef.current?.({ [type === 'entrance' ? 'ingress' : 'egress']: [] });
    }, []);

    const attachMarkerListeners = useCallback((marker, type) => {
        const google = googleRef.current;
        google.maps.event.addListener(marker, 'dragend', () => {
            const pos = marker.getPosition();
            const pt = { lat: pos.lat(), lng: pos.lng() };
            if (type === 'entrance') {
                setIngressState([pt]);
                ingressRef.current = [pt];
                scheduleAutosaveRef.current?.({ ingress: [pt] });
            } else {
                setEgressState([pt]);
                egressRef.current = [pt];
                scheduleAutosaveRef.current?.({ egress: [pt] });
            }
        });
        google.maps.event.addListener(marker, 'click', (e) => {
            e.stop();
            selectedMarkerRef.current = type;
            deselectPolygonRef.current?.();
        });
    }, []);

    const placeEntranceMarker = useCallback((pt, { skipAutosave = false } = {}) => {
        if (!mapRef.current || !googleRef.current) return;
        if (entranceMarkerRef.current) {
            googleRef.current.maps.event.clearInstanceListeners(entranceMarkerRef.current);
            entranceMarkerRef.current.setMap(null);
        }
        const marker = new googleRef.current.maps.Marker({
            position: pt,
            map: mapRef.current,
            icon: {
                url: greenLocationMarker,
                scaledSize: new googleRef.current.maps.Size(32, 32),
                anchor: new googleRef.current.maps.Point(16, 32),
            },
            draggable: true,
            zIndex: 1000,
        });
        attachMarkerListeners(marker, 'entrance');
        entranceMarkerRef.current = marker;
        setIngressState([pt]);
        ingressRef.current = [pt];
        if (!skipAutosave) scheduleAutosaveRef.current?.({ ingress: [pt] });
    }, [attachMarkerListeners]);

    const placeEgressMarker = useCallback((pt, { skipAutosave = false } = {}) => {
        if (!mapRef.current || !googleRef.current) return;
        if (egressMarkerRef.current) {
            googleRef.current.maps.event.clearInstanceListeners(egressMarkerRef.current);
            egressMarkerRef.current.setMap(null);
        }
        const marker = new googleRef.current.maps.Marker({
            position: pt,
            map: mapRef.current,
            icon: {
                url: redLocationMarker,
                scaledSize: new googleRef.current.maps.Size(32, 32),
                anchor: new googleRef.current.maps.Point(16, 32),
            },
            draggable: true,
            zIndex: 1000,
        });
        attachMarkerListeners(marker, 'egress');
        egressMarkerRef.current = marker;
        setEgressState([pt]);
        egressRef.current = [pt];
        if (!skipAutosave) scheduleAutosaveRef.current?.({ egress: [pt] });
    }, [attachMarkerListeners]);

    const applyDefaultMarkers = useCallback((geofenceCoords, { skipAutosave = false } = {}) => {
        const propertyCenter = addressData?.lat != null && addressData?.lng != null
            ? { lat: addressData.lat, lng: addressData.lng }
            : null;
        const { ingress: ing, egress: eg } = computeDefaultIngressEgress(geofenceCoords, propertyCenter);

        if (mapRef.current && googleRef.current) {
            if (ing[0]) placeEntranceMarker(ing[0], { skipAutosave: true });
            if (eg[0]) placeEgressMarker(eg[0], { skipAutosave: true });
        } else {
            setIngressState(ing);
            setEgressState(eg);
            ingressRef.current = ing;
            egressRef.current = eg;
        }

        if (!skipAutosave) {
            flushAutosave({
                ...(geofenceCoords?.length ? { geofence: geofenceCoords } : {}),
                ingress: ing,
                egress: eg,
            });
        }
        return { ingress: ing, egress: eg };
    }, [addressData, placeEntranceMarker, placeEgressMarker, flushAutosave]);

    const syncAllPaths = useCallback((markUnsaved = false) => {
        const allCoords = coordsFromPolygons(polygonsRef);
        setPolygons(allCoords);
        onPolygonChange?.(allCoords);
        onPolygonsChange?.(allCoords);
        if (markUnsaved) scheduleAutosave({ geofence: allCoords });
        rebuildSelectVertexMarkersRef.current?.();
    }, [onPolygonChange, onPolygonsChange, scheduleAutosave]);

    const clearSelectVertexMarkers = useCallback(() => {
        if (!googleRef.current) return;
        selectVertexMarkerListenersRef.current.forEach(l =>
            googleRef.current.maps.event.removeListener(l),
        );
        selectVertexMarkerListenersRef.current = [];
        selectVertexMarkersRef.current.forEach(m => {
            googleRef.current.maps.event.clearInstanceListeners(m);
            m.setMap(null);
        });
        selectVertexMarkersRef.current = [];
    }, []);

    const clearGradientOverlays = useCallback(() => {
        gradientPolylinesRef.current.forEach((line) => line.setMap(null));
        gradientPolylinesRef.current = [];
    }, []);

    const clearVertexSelection = useCallback(() => {
        selectedVertexRef.current = null;
        setVertexDeleteBtnPos(null);
        clearGradientOverlays();
        if (activeToolRef.current === 'select-vertex') {
            rebuildSelectVertexMarkersRef.current?.();
        }
    }, [clearGradientOverlays]);

    const computeVertexDeleteBtnPos = useCallback((poly, vertexIndex) => {
        const map = mapRef.current;
        if (!map || !mapDivRef.current) return null;
        const bounds = map.getBounds();
        if (!bounds) return null;
        const path = poly.getPath();
        const pt = path.getAt(vertexIndex);
        if (!pt) return null;
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        const latRange = ne.lat() - sw.lat();
        const lngRange = ne.lng() - sw.lng();
        if (latRange === 0 || lngRange === 0) return null;
        const { offsetWidth: w, offsetHeight: h } = mapDivRef.current;
        return {
            x: Math.round(((pt.lng() - sw.lng()) / lngRange) * w),
            y: Math.round(((ne.lat() - pt.lat()) / latRange) * h),
        };
    }, []);

    const updateGradientOverlays = useCallback((poly, vertexIndex) => {
        clearGradientOverlays();
        if (!googleRef.current || !mapRef.current || !poly) return;
        const path = poly.getPath();
        const n = path.getLength();
        if (n < 2) return;

        const drawEdge = (fromIdx, toIdx) => {
            const a = path.getAt(fromIdx);
            const b = path.getAt(toIdx);
            const segments = 8;
            for (let s = 0; s < segments; s += 1) {
                const t0 = s / segments;
                const t1 = (s + 1) / segments;
                const lat0 = a.lat() + (b.lat() - a.lat()) * t0;
                const lng0 = a.lng() + (b.lng() - a.lng()) * t0;
                const lat1 = a.lat() + (b.lat() - a.lat()) * t1;
                const lng1 = a.lng() + (b.lng() - a.lng()) * t1;
                // Orange ANCHOR (closest to selected vertex) → blue AT the neighbor.
                // Selected dot = bright; neighbor = cool. fromIdx is always the selected vertex.
                const segColor = lerpColor(EDGE_GRADIENT_ORANGE, EDGE_GRADIENT_BLUE, t0);
                const line = new googleRef.current.maps.Polyline({
                    path: [{ lat: lat0, lng: lng0 }, { lat: lat1, lng: lng1 }],
                    strokeColor: segColor,
                    strokeOpacity: 1,
                    strokeWeight: 4,
                    zIndex: 500,
                    clickable: false,
                });
                line.setMap(mapRef.current);
                gradientPolylinesRef.current.push(line);
            }
        };

        const prevIdx = (vertexIndex - 1 + n) % n;
        const nextIdx = (vertexIndex + 1) % n;
        drawEdge(vertexIndex, prevIdx);
        drawEdge(vertexIndex, nextIdx);
    }, [clearGradientOverlays]);

    const selectVertex = useCallback((polyIdx, vertexIndex) => {
        const poly = polygonsRef.current[polyIdx];
        if (!poly) return;
        selectedVertexRef.current = { polyIdx, vertexIndex };
        deselectPolygon();
        selectedMarkerRef.current = null;
        updateGradientOverlays(poly, vertexIndex);
        setTimeout(() => {
            setVertexDeleteBtnPos(computeVertexDeleteBtnPos(poly, vertexIndex));
        }, 0);
        rebuildSelectVertexMarkersRef.current?.();
    }, [deselectPolygon, updateGradientOverlays, computeVertexDeleteBtnPos]);

    const rebuildSelectVertexMarkers = useCallback(() => {
        if (activeToolRef.current !== 'select-vertex') return;
        clearSelectVertexMarkers();
        if (!googleRef.current || !mapRef.current) return;

        const google = googleRef.current;
        const layerFilter = activeLayerRef.current;
        const layerIndices = layersRef.current;
        const selected = selectedVertexRef.current;
        const markers = [];
        const listeners = [];

        polygonsRef.current.forEach((poly, polyIdx) => {
            if (layerFilter !== 'all' && layerIndices[polyIdx] !== layerFilter) return;
            const path = poly.getPath();
            for (let i = 0; i < path.getLength(); i += 1) {
                const latlng = path.getAt(i);
                const isSelected = selected?.polyIdx === polyIdx && selected?.vertexIndex === i;
                const marker = new google.maps.Marker({
                    position: latlng,
                    map: mapRef.current,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: isSelected ? 7 : 6,
                        fillColor: isSelected ? EDGE_GRADIENT_ORANGE : '#FFFFFF',
                        fillOpacity: 1,
                        strokeColor: isSelected ? EDGE_GRADIENT_ORANGE : '#2254A3',
                        strokeWeight: 2,
                    },
                    draggable: false,
                    clickable: true,
                    cursor: 'pointer',
                    zIndex: 600,
                });
                const listener = marker.addListener('click', () => {
                    selectVertexRef.current?.(polyIdx, i);
                });
                listeners.push(listener);
                markers.push(marker);
            }
        });
        selectVertexMarkersRef.current = markers;
        selectVertexMarkerListenersRef.current = listeners;
    }, [clearSelectVertexMarkers]);

    const deleteSelectedVertex = useCallback(() => {
        const sel = selectedVertexRef.current;
        if (!sel) return;
        const poly = polygonsRef.current[sel.polyIdx];
        if (!poly) return;
        const path = poly.getPath();
        if (path.getLength() <= 3) return;
        path.removeAt(sel.vertexIndex);
        clearVertexSelection();
        syncAllPaths(true);
        if (selectedPolyIdxRef.current === sel.polyIdx) {
            setTimeout(() => {
                setDeleteBtnPos(computeDeleteBtnPos(poly) ?? null);
            }, 0);
        }
    }, [clearVertexSelection, syncAllPaths, computeDeleteBtnPos]);

    const applyToolAndLayerStyles = useCallback(() => {
        const tool = activeToolRef.current;
        const layerFilter = activeLayerRef.current;
        const layerIndices = layersRef.current;

        polygonsRef.current.forEach((poly, idx) => {
            const dimmed = layerFilter !== 'all'
                && layerIndices[idx] !== layerFilter;
            const isSelected = selectedPolyIdxRef.current === idx;
            const baseFill = isSelected ? 0.45 : 0.35;
            poly.setOptions({
                fillOpacity: dimmed ? baseFill * 0.25 : baseFill,
                strokeOpacity: dimmed ? 0.25 : 1,
                clickable: !dimmed,
                editable: POLYGON_EDITABLE_TOOLS.has(tool) && !dimmed,
            });
        });

        if (mapDivRef.current) {
            if (tool === 'entrance') {
                mapDivRef.current.style.cursor = ENTRANCE_CURSOR;
            } else if (tool === 'exit') {
                mapDivRef.current.style.cursor = EXIT_CURSOR;
            } else if (tool === 'draw') {
                mapDivRef.current.style.cursor = 'crosshair';
            } else {
                mapDivRef.current.style.cursor = '';
            }
        }
    }, []);

    const deleteSelectedPolygon = useCallback(() => {
        const idx = selectedPolyIdxRef.current;
        if (idx === null) return;
        const poly = polygonsRef.current[idx];
        if (!poly) return;
        googleRef.current.maps.event.clearInstanceListeners(poly);
        poly.setMap(null);
        polygonsRef.current.splice(idx, 1);
        selectedPolyIdxRef.current = null;
        setDeleteBtnPos(null);
        syncAllPaths(true);
    }, [syncAllPaths]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const tag = e.target?.tagName;
            const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable;
            if (isEditable) return;

            if (e.key === 'Escape') {
                if (selectedMarkerRef.current) {
                    e.preventDefault();
                    selectedMarkerRef.current = null;
                    return;
                }
                if (selectedVertexRef.current) {
                    e.preventDefault();
                    clearVertexSelection();
                    return;
                }
                if (customDrawRef.current) {
                    e.preventDefault();
                    cancelCustomDraw();
                    return;
                }
                if (activeToolRef.current !== 'drag') {
                    e.preventDefault();
                    onChangeTool?.('drag');
                }
                return;
            }
            if (customDrawRef.current && e.key === 'Backspace') {
                e.preventDefault();
                const session = customDrawRef.current;
                if (session.vertices.length === 0) return;
                session.vertices.pop();
                const marker = session.markers.pop();
                marker?.setMap(null);
                syncCustomDrawPolylines(session);
                return;
            }
            if ((e.key === 'Delete' || e.key === 'Backspace') &&
                selectedPolyIdxRef.current !== null) {
                e.preventDefault();
                deleteSelectedPolygon();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [deleteSelectedPolygon, cancelCustomDraw, clearVertexSelection, onChangeTool, syncCustomDrawPolylines]);

    const attachPolygonListeners = useCallback((poly) => {
        const path = poly.getPath();
        ['insert_at', 'remove_at', 'set_at'].forEach(ev => {
            const l = googleRef.current.maps.event.addListener(path, ev, () => syncAllPaths(true));
            pathListenersRef.current.push(l);
        });
        const rcl = googleRef.current.maps.event.addListener(poly, 'rightclick', (e) => {
            const tool = activeToolRef.current;
            if (tool === 'select-vertex' && e.vertex != null) {
                const idx = polygonsRef.current.indexOf(poly);
                if (idx !== -1) selectVertex(idx, e.vertex);
                return;
            }
            if (e.vertex != null) {
                const p = poly.getPath();
                if (p.getLength() > 3) p.removeAt(e.vertex);
            }
        });
        pathListenersRef.current.push(rcl);
        const cl = googleRef.current.maps.event.addListener(poly, 'click', (e) => {
            const idx = polygonsRef.current.indexOf(poly);
            if (idx === -1) return;
            const tool = activeToolRef.current;
            const layerFilter = activeLayerRef.current;
            const layerIndices = layersRef.current;
            if (layerFilter !== 'all' && layerIndices[idx] !== layerFilter) return;

            if (tool === 'select-vertex' && e.vertex != null) {
                selectVertex(idx, e.vertex);
                return;
            }
            if (tool === 'select-polygon' || tool === 'drag') {
                if (selectedPolyIdxRef.current === idx) {
                    deselectPolygon();
                } else {
                    clearVertexSelection();
                    doSelectPolygon(idx);
                }
            }
        });
        pathListenersRef.current.push(cl);
    }, [syncAllPaths, deselectPolygon, doSelectPolygon, selectVertex, clearVertexSelection]);

    const createGmapsPolygon = useCallback((coords) => {
        const poly = new googleRef.current.maps.Polygon({
            paths: coords,
            strokeColor: '#2254A3',
            strokeOpacity: 1,
            strokeWeight: 2,
            fillColor: '#1E52A3',
            fillOpacity: 0.35,
            editable: true,
        });
        poly.setMap(mapRef.current);
        attachPolygonListeners(poly);
        return poly;
    }, [attachPolygonListeners]);

    const drawAllPolygons = useCallback((coordsArray) => {
        if (!mapRef.current || !googleRef.current || !coordsArray?.length) return;
        hadPolygonsEverRef.current = true;
        destroyAllPolygons();
        coordsArray.forEach(coords => {
            if (!Array.isArray(coords) || coords.length < 3) return;
            const poly = createGmapsPolygon(coords);
            polygonsRef.current.push(poly);
        });
        syncAllPaths(false);
        applyToolAndLayerStyles();
    }, [destroyAllPolygons, createGmapsPolygon, syncAllPaths, applyToolAndLayerStyles]);

    const handleBasicPolygonDrop = useCallback((event) => {
        if (!mapRef.current || !googleRef.current) return;
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        const coords = basicRectangleAt(lat, lng);
        const poly = createGmapsPolygon(coords);
        polygonsRef.current.push(poly);
        deselectPolygon();
        clearVertexSelection();
        const allCoords = coordsFromPolygons(polygonsRef);
        setPolygons(allCoords);
        onPolygonChange?.(allCoords);
        onPolygonsChange?.(allCoords);
        onChangeTool?.('drag');
        clearTimeout(autosaveTimerRef.current);
        const isFirstPolygonEver = !hadPolygonsEverRef.current;
        hadPolygonsEverRef.current = true;
        if (isFirstPolygonEver) {
            const markers = applyDefaultMarkers(allCoords, { skipAutosave: true });
            flushAutosave({ geofence: allCoords, ...markers });
        } else {
            flushAutosave({ geofence: allCoords });
        }
        applyToolAndLayerStyles();
    }, [
        createGmapsPolygon,
        deselectPolygon,
        clearVertexSelection,
        onPolygonChange,
        onPolygonsChange,
        onChangeTool,
        applyDefaultMarkers,
        flushAutosave,
        applyToolAndLayerStyles,
    ]);

    const handleEntranceDrop = useCallback((event) => {
        const pt = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        placeEntranceMarker(pt);
        onChangeTool?.('drag');
        scheduleAutosaveRef.current?.({
            ingress: [pt],
            markIngressEgressHostSet: true,
        });
    }, [placeEntranceMarker, onChangeTool]);

    const handleExitDrop = useCallback((event) => {
        const pt = { lat: event.latLng.lat(), lng: event.latLng.lng() };
        placeEgressMarker(pt);
        onChangeTool?.('drag');
        scheduleAutosaveRef.current?.({
            egress: [pt],
            markIngressEgressHostSet: true,
        });
    }, [placeEgressMarker, onChangeTool]);

    const onDrawComplete = useCallback((coords) => {
        setOsmSource(false);

        const poly = createGmapsPolygon(coords);
        polygonsRef.current.push(poly);
        deselectPolygon();

        const allCoords = coordsFromPolygons(polygonsRef);
        setPolygons(allCoords);
        onPolygonChange?.(allCoords);
        onPolygonsChange?.(allCoords);
        clearTimeout(autosaveTimerRef.current);
        const isFirstPolygonEver = !hadPolygonsEverRef.current;
        hadPolygonsEverRef.current = true;
        if (isFirstPolygonEver) {
            const markers = applyDefaultMarkers(allCoords, { skipAutosave: true });
            flushAutosave({ geofence: allCoords, ...markers });
        } else {
            flushAutosave({ geofence: allCoords });
        }
        applyToolAndLayerStyles();
    }, [createGmapsPolygon, onPolygonChange, onPolygonsChange, flushAutosave, deselectPolygon, applyDefaultMarkers, applyToolAndLayerStyles]);

    const finishCustomDraw = useCallback(() => {
        const session = customDrawRef.current;
        if (!session) return;
        if (session.vertices.length < 3) {
            toastWarn('Add at least 3 points to finish the boundary.');
            return;
        }
        const coords = [...session.vertices];
        destroyCustomDraw();
        onDrawComplete(coords);
    }, [destroyCustomDraw, onDrawComplete]);

    const addCustomDrawVertex = useCallback((latLng) => {
        const session = customDrawRef.current;
        if (!session || !googleRef.current || !mapRef.current) return;

        const vertex = { lat: latLng.lat(), lng: latLng.lng() };
        session.vertices.push(vertex);

        const google = googleRef.current;
        const isFirstVertex = session.vertices.length === 1;
        const marker = new google.maps.Marker({
            position: vertex,
            map: mapRef.current,
            clickable: isFirstVertex,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                scale: isFirstVertex ? 7 : 4,
                fillColor: '#2254A3',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 1,
            },
        });

        if (isFirstVertex) {
            const defaultIcon = {
                path: google.maps.SymbolPath.CIRCLE,
                scale: 7,
                fillColor: '#2254A3',
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 1,
            };
            const hoverIcon = {
                ...defaultIcon,
                scale: 9,
                fillColor: '#16A34A',
            };
            google.maps.event.addListener(marker, 'mouseover', () => {
                marker.setIcon(hoverIcon);
            });
            google.maps.event.addListener(marker, 'mouseout', () => {
                marker.setIcon(defaultIcon);
            });
            google.maps.event.addListener(marker, 'click', () => {
                const s = customDrawRef.current;
                if (s && s.vertices.length >= 3) {
                    finishCustomDrawRef.current?.();
                }
            });
        }

        session.markers.push(marker);
        syncCustomDrawPolylines(session);
    }, [syncCustomDrawPolylines]);

    const startCustomDraw = useCallback(() => {
        if (!mapRef.current || !googleRef.current?.maps) return;

        destroyCustomDraw();

        const google = googleRef.current;
        mapRef.current.setOptions({
            draggableCursor: 'crosshair',
            draggingCursor: 'crosshair',
            disableDoubleClickZoom: true,
        });

        const polyline = new google.maps.Polyline({
            map: mapRef.current,
            path: [],
            strokeColor: '#2254A3',
            strokeOpacity: 0.95,
            strokeWeight: 3,
            zIndex: 2,
            clickable: false,
        });

        const halo = new google.maps.Polyline({
            map: mapRef.current,
            path: [],
            strokeColor: '#ffffff',
            strokeOpacity: 0.85,
            strokeWeight: 6,
            zIndex: 1,
            clickable: false,
        });

        const preview = new google.maps.Polyline({
            map: mapRef.current,
            path: [],
            strokeOpacity: 0,
            zIndex: 3,
            clickable: false,
            icons: [{
                icon: {
                    path: 'M 0,-1 0,1',
                    strokeOpacity: 1,
                    strokeColor: '#2254A3',
                    scale: 3,
                },
                offset: '0',
                repeat: '12px',
            }],
        });

        const session = {
            vertices: [],
            polyline,
            halo,
            preview,
            markers: [],
            listeners: [],
        };

        const dblClickListener = google.maps.event.addListener(
            mapRef.current,
            'dblclick',
            (e) => {
                e.stop();
                finishCustomDrawRef.current?.();
            },
        );
        session.listeners.push(dblClickListener);

        const mouseMoveListener = google.maps.event.addListener(
            mapRef.current,
            'mousemove',
            (e) => {
                const s = customDrawRef.current;
                if (!s || s.vertices.length < 1) return;
                const last = s.vertices[s.vertices.length - 1];
                s.preview.setPath([
                    new google.maps.LatLng(last.lat, last.lng),
                    e.latLng,
                ]);
            },
        );
        session.listeners.push(mouseMoveListener);

        const contextMenuListener = google.maps.event.addListener(
            mapRef.current,
            'contextmenu',
            () => {
                cancelCustomDraw();
            },
        );
        session.listeners.push(contextMenuListener);

        customDrawRef.current = session;
    }, [destroyCustomDraw, cancelCustomDraw]);

    const queryOsm = useCallback(async ({ force = false, isRetry = false } = {}) => {
        if (!listingId || osmInFlightRef.current) return;
        osmInFlightRef.current = true;
        setOsmLoading(true);
        if (!isRetry) {
            geofenceToastShownRef.current = null;
        }
        let willRetry = false;
        try {
            const body = force ? { force: true } : {};
            const res = await axios.post(
                `/listings/${listingId}/generate-geofence`,
                body,
                { timeout: 35000 },
            );
            const polys = res.data.geofence ?? [];
            const ingressPts = res.data.ingress ?? [];
            const egressPts = res.data.egress ?? [];
            if (polys.length >= 1) {
                setOsmSource(true);
                reportOsmResult(true);
                drawAllPolygons(polys);
                clearTimeout(autosaveTimerRef.current);
                if (ingressPts.length && egressPts.length) {
                    if (ingressPts[0]) placeEntranceMarker(ingressPts[0], { skipAutosave: true });
                    if (egressPts[0]) placeEgressMarker(egressPts[0], { skipAutosave: true });
                    flushAutosave({ geofence: polys, ingress: ingressPts, egress: egressPts });
                } else {
                    applyDefaultMarkers(polys, { skipAutosave: false });
                }
            } else {
                const reason = res.data?.reason ?? 'unknown';
                if (geofenceToastShownRef.current !== reason) {
                    geofenceToastShownRef.current = reason;
                    toastWarn(geofenceFailureMessage(reason));
                }
                setOsmSource(false);
                reportOsmResult(false);
                applyDefaultMarkers([], { skipAutosave: false });
                onChangeTool?.('draw');
            }
        } catch (err) {
            const isTimeout = err?.code === 'ECONNABORTED';
            const isServerError = err?.response?.status >= 500;
            if (!isRetry && (isTimeout || isServerError)) {
                willRetry = true;
                await new Promise((resolve) => { setTimeout(resolve, 1500); });
                return queryOsm({ force, isRetry: true });
            }
            if (geofenceToastShownRef.current !== 'query_failed') {
                geofenceToastShownRef.current = 'query_failed';
                toastWarn(GEOFENCE_QUERY_FAILED);
            }
            setOsmSource(false);
            reportOsmResult(false);
            applyDefaultMarkers([], { skipAutosave: false });
            onChangeTool?.('draw');
        } finally {
            if (!willRetry) {
                osmInFlightRef.current = false;
                setOsmLoading(false);
            }
        }
    }, [listingId, drawAllPolygons, startCustomDraw, flushAutosave, applyDefaultMarkers, placeEntranceMarker, placeEgressMarker, onChangeTool]);

    const handleRegenerate = useCallback(async () => {
        clearTimeout(autosaveTimerRef.current);
        destroyCustomDraw();
        destroyAllPolygons();
        setPolygons([]);
        onPolygonChange?.([]);
        await queryOsm({ force: true });
    }, [destroyCustomDraw, destroyAllPolygons, onPolygonChange, queryOsm]);

    useEffect(() => {
        onRegenUpdate?.(osmLoading, handleRegenerate);
    }, [osmLoading, handleRegenerate, onRegenUpdate]);

    useEffect(() => {
        polygonsExistRef.current = polygons.length > 0;
    }, [polygons]);

    useEffect(() => {
        if (initialPolygonAppliedRef.current) return;
        const normalized = normalizeGeofence(initialPolygon);
        if (normalized.length === 0) return;
        setPolygons(prev => (geofencesEqual(prev, normalized) ? prev : normalized));
        initialPolygonAppliedRef.current = true;
    }, [initialPolygon]);

    useEffect(() => {
        if (initialIngressEgressAppliedRef.current) return;
        const nextIngress = Array.isArray(initialIngress) ? initialIngress : [];
        const nextEgress = Array.isArray(initialEgress) ? initialEgress : [];
        setIngressState(nextIngress);
        setEgressState(nextEgress);
        ingressRef.current = nextIngress;
        egressRef.current = nextEgress;
        initialIngressEgressAppliedRef.current = true;
    }, [initialIngress, initialEgress]);

    useEffect(() => {
        startCustomDrawRef.current = startCustomDraw;
        finishCustomDrawRef.current = finishCustomDraw;
        addCustomDrawVertexRef.current = addCustomDrawVertex;
        deselectPolygonRef.current = deselectPolygon;
        computeDeleteBtnPosRef.current = computeDeleteBtnPos;
        cancelDrawingRef.current = cancelCustomDraw;
        placeEntranceMarkerRef.current = placeEntranceMarker;
        placeEgressMarkerRef.current = placeEgressMarker;
        handleBasicPolygonDropRef.current = handleBasicPolygonDrop;
        handleEntranceDropRef.current = handleEntranceDrop;
        handleExitDropRef.current = handleExitDrop;
        clearVertexSelectionRef.current = clearVertexSelection;
        selectVertexRef.current = selectVertex;
        rebuildSelectVertexMarkersRef.current = rebuildSelectVertexMarkers;
    });

    useEffect(() => {
        if (!mapReady) return;
        if (activeTool !== 'select-vertex') {
            clearSelectVertexMarkers();
            return;
        }
        rebuildSelectVertexMarkers();
    }, [activeTool, activeLayer, layers, polygons, mapReady, clearSelectVertexMarkers, rebuildSelectVertexMarkers]);

    useEffect(() => {
        const prev = prevActiveToolRef.current;
        if (prev === 'draw' && activeTool !== 'draw' && customDrawRef.current) {
            cancelCustomDraw();
        }
        prevActiveToolRef.current = activeTool;

        if (activeTool === 'draw') {
            startCustomDraw();
        } else {
            destroyCustomDraw();
        }

        if (activeTool !== 'select-vertex') {
            clearVertexSelection();
        }
        if (activeTool !== 'select-polygon' && activeTool !== 'drag') {
            deselectPolygon();
        }

        applyToolAndLayerStyles();
    }, [
        activeTool,
        activeLayer,
        layers,
        startCustomDraw,
        destroyCustomDraw,
        cancelCustomDraw,
        clearVertexSelection,
        deselectPolygon,
        applyToolAndLayerStyles,
    ]);

    const markersInitializedRef = useRef(false);

    useEffect(() => {
        if (!mapReady || !googleRef.current || !mapRef.current) return;
        if (markersInitializedRef.current) return;
        markersInitializedRef.current = true;

        if (ingressRef.current?.[0] || egressRef.current?.[0]) {
            if (ingressRef.current?.[0]) {
                placeEntranceMarker(ingressRef.current[0], { skipAutosave: true });
            }
            if (egressRef.current?.[0]) {
                placeEgressMarker(egressRef.current[0], { skipAutosave: true });
            }
            return;
        }

        const gf = normalizeGeofence(initialPolygon);
        if (gf.length || addressData?.lat != null) {
            applyDefaultMarkers(gf, { skipAutosave: false });
        }
    // One-shot marker placement on first map ready; ignore later prop churn while modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapReady]);

    useEffect(() => {
        let cancelled = false;
        let clickListener = null;
        let boundsListener = null;
        let rightClickListener = null;

        (async () => {
            try {
                const google = await loadGoogleMapsApi(GOOGLE_API_KEY);
                if (cancelled || !google?.maps || !mapDivRef.current) return;

                googleRef.current = google;
                mapRef.current = new google.maps.Map(mapDivRef.current, {
                    mapTypeId: 'satellite',
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                });

                clickListener = google.maps.event.addListener(mapRef.current, 'click', (e) => {
                    const tool = activeToolRef.current;
                    if (tool === 'basic') {
                        handleBasicPolygonDropRef.current?.(e);
                        return;
                    }
                    if (tool === 'entrance') {
                        handleEntranceDropRef.current?.(e);
                        return;
                    }
                    if (tool === 'exit') {
                        handleExitDropRef.current?.(e);
                        return;
                    }
                    if (tool === 'draw') {
                        if (customDrawRef.current) {
                            addCustomDrawVertexRef.current?.(e.latLng);
                        } else {
                            startCustomDrawRef.current?.();
                        }
                        return;
                    }
                    if (tool === 'drag') {
                        if (selectedPolyIdxRef.current !== null) {
                            deselectPolygonRef.current?.();
                        }
                        clearVertexSelectionRef.current?.();
                        return;
                    }
                    if (tool === 'select-vertex' || tool === 'select-polygon') {
                        if (e.vertex == null && selectedPolyIdxRef.current !== null) {
                            deselectPolygonRef.current?.();
                        }
                        if (e.vertex == null) clearVertexSelectionRef.current?.();
                    }
                });

                rightClickListener = google.maps.event.addListener(mapRef.current, 'rightclick', () => {
                    if (customDrawRef.current) {
                        cancelDrawingRef.current?.();
                    }
                });

                boundsListener = google.maps.event.addListener(mapRef.current, 'bounds_changed', () => {
                    if (selectedPolyIdxRef.current === null) return;
                    const poly = polygonsRef.current[selectedPolyIdxRef.current];
                    if (!poly) return;
                    setTimeout(() => {
                        setDeleteBtnPos(computeDeleteBtnPosRef.current?.(poly) ?? null);
                    }, 0);
                });

                setMapReady(true);

                requestAnimationFrame(() => {
                    if (cancelled) return;
                    if (!mapRef.current) return;
                    google.maps.event.trigger(mapRef.current, 'resize');
                });
            } catch (err) {
                console.error('GeofenceEditor map init failed:', err);
                if (!cancelled) setMapError(true);
            }
        })();

        return () => {
            cancelled = true;
            clearTimeout(autosaveTimerRef.current);
            if (clickListener) googleRef.current?.maps?.event.removeListener(clickListener);
            if (boundsListener) googleRef.current?.maps?.event.removeListener(boundsListener);
            if (rightClickListener) googleRef.current?.maps?.event.removeListener(rightClickListener);
            destroyAllPolygons();
            destroyMarkers();
            destroyCustomDraw();
            markersInitializedRef.current = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!mapReady || !mapRef.current) return;
        const addressKey = addressData?.lat != null && addressData?.lng != null
            ? `${addressData.lat.toFixed(6)},${addressData.lng.toFixed(6)}`
            : null;
        if (!addressKey || fitDoneForAddressRef.current === addressKey) return;

        const fitBounds = boundsFromAddress(addressData?.bounds);
        if (fitBounds) {
            mapRef.current.fitBounds(fitBounds);
        } else {
            mapRef.current.setCenter({ lat: addressData.lat, lng: addressData.lng });
            mapRef.current.setZoom(18);
        }
        fitDoneForAddressRef.current = addressKey;
    }, [mapReady, addressData?.bounds, addressData?.lat, addressData?.lng]);

    useEffect(() => {
        if (!mapReady || !mapRef.current || !googleRef.current || !mapDivRef.current) return;

        const map = mapRef.current;
        const google = googleRef.current;
        const el = mapDivRef.current;

        const onResize = () => {
            google.maps.event.trigger(map, 'resize');
        };

        const observer = new ResizeObserver(onResize);
        observer.observe(el);
        onResize();

        return () => observer.disconnect();
    }, [mapReady]);

    useEffect(() => {
        if (!mapReady || !initialPolygon?.length) return;
        if (initialDrawDoneRef.current) return;
        const normalized = normalizeGeofence(initialPolygon);
        if (normalized.length > 0) {
            initialDrawDoneRef.current = true;
            drawAllPolygons(normalized);
        }
    }, [mapReady, initialPolygon, drawAllPolygons]);

    useEffect(() => {
        if (!mapReady || osmQueryFiredRef.current) return;
        if (normalizeGeofence(initialPolygon).length > 0) {
            // Existing boundary — nothing to fetch, but the consumer still
            // needs a definitive "AI has one" signal.
            reportOsmResult(true);
            return;
        }
        if (skipAutoQuery) {
            osmQueryFiredRef.current = true;
            // Caller opted out of the auto-query; the initialPolygon is the
            // only signal available. Empty here → treat as no AI boundary.
            reportOsmResult(false);
            if (!ingressRef.current?.length && addressData?.lat != null) {
                applyDefaultMarkers([], { skipAutosave: false });
            }
            return;
        }

        osmQueryFiredRef.current = true;
        queryOsm();
    }, [mapReady, initialPolygon, queryOsm, skipAutoQuery, applyDefaultMarkers, addressData, onChangeTool, reportOsmResult]);

    useEffect(() => {
        if (mapError) toastError(GEOFENCE_MAP_LOAD_FAILED);
    }, [mapError]);

    const hasPolygon = polygons.length > 0;
    const mapHeight = embedded ? '100%' : 320;

    return (
        <div className={`flex flex-col ${embedded ? 'h-full' : 'gap-3'}`}>
            {!embedded && (
                <h3 className="text-sm font-bold text-gray-900">Parking boundary</h3>
            )}

            {mapError ? (
                <div
                    className={`relative w-full shrink-0 bg-gray-100 ${embedded ? '' : 'rounded-lg border border-gray-200'}`}
                    style={{ height: mapHeight, minHeight: mapHeight }}
                    aria-hidden="true"
                />
            ) : (
                <div
                    className={`relative w-full shrink-0 ${embedded ? '' : 'rounded-lg border border-gray-200'}`}
                    style={{ height: mapHeight, minHeight: mapHeight }}
                >
                    <div
                        ref={mapDivRef}
                        className={`h-full w-full overflow-hidden ${embedded ? '' : 'rounded-lg'}`}
                        style={{ width: '100%', height: mapHeight }}
                        aria-label="Parking boundary map"
                    />
                    {osmLoading && (
                        <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-black/20 pointer-events-auto">
                            <span className="rounded-full bg-black/50 px-3 py-1.5 text-sm font-medium text-white">
                                Looking up parking boundary…
                            </span>
                        </div>
                    )}
                    {deleteBtnPos && (
                        <button
                            type="button"
                            className="geofence-delete-section-btn"
                            style={{
                                left: deleteBtnPos.x,
                                top: Math.max(8, deleteBtnPos.y - 44),
                                transform: 'translateX(-50%)',
                            }}
                            onClick={deleteSelectedPolygon}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                            </svg>
                            Delete section
                        </button>
                    )}
                    {vertexDeleteBtnPos && (
                        <button
                            type="button"
                            className="geofence-delete-section-btn"
                            style={{
                                left: vertexDeleteBtnPos.x,
                                top: vertexDeleteBtnPos.y < 60
                                    ? vertexDeleteBtnPos.y + 12
                                    : Math.max(8, vertexDeleteBtnPos.y - 44),
                                transform: 'translateX(-50%)',
                            }}
                            onClick={deleteSelectedVertex}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14H6L5 6" />
                                <path d="M10 11v6M14 11v6" />
                                <path d="M9 6V4h6v2" />
                            </svg>
                            Delete vertex
                        </button>
                    )}
                </div>
            )}

            {embedded && osmSource && hasPolygon && !osmLoading && (
                <p className="px-6 pb-3 text-xs text-gray-400">
                    ✓ {polygons.length > 1
                        ? `${polygons.length} boundary sections`
                        : 'Boundary'
                    } sourced from OpenStreetMap
                </p>
            )}

        </div>
    );
};

export default GeofenceEditor;
